import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, viewChild } from '@angular/core';
import { NgSelectComponent } from '@ng-select/ng-select';
import { Observable, Subject, combineLatest, takeUntil } from 'rxjs';

import { AccountFacade } from 'ish-core/facades/account.facade';
import { AppFacade } from 'ish-core/facades/app.facade';
import { CheckoutFacade } from 'ish-core/facades/checkout.facade';
import { Customer } from 'ish-core/models/customer/customer.model';
import { User } from 'ish-core/models/user/user.model';
import { DeviceType } from 'ish-core/models/viewtype/viewtype.types';
import { GenerateLazyComponent } from 'ish-core/utils/module-loader/generate-lazy-component.decorator';

import { SalesRepFacade } from '../../facades/sales-rep.facade';
import { SalesRepCustomerSwitchConfirmationModalComponent } from '../customer-switch-confirmation-modal/sales-rep-switch-confirmation-modal.component';

interface customerToDisplay {
  customerNo: string;
  companyName: string;
}

@Component({
  selector: 'custom-sales-rep-users-list',
  templateUrl: './sales-rep-users-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
@GenerateLazyComponent()
export class SalesRepUsersListComponent implements OnInit, OnDestroy {
  readonly confirmationModal = viewChild<SalesRepCustomerSwitchConfirmationModalComponent>('confirmationModal');
  readonly customerSelect = viewChild<NgSelectComponent>('customerSelect');

  user$: Observable<User>;
  customer$: Observable<Customer>;
  customersToDisplay: customerToDisplay[];
  currentCustomer: string;
  selectedCustomer: customerToDisplay;
  selectedValue = ''; // Track the current selected value
  private destroy$ = new Subject<void>();
  deviceType$: Observable<DeviceType>;
  loading$: Observable<boolean>;
  itemsCount$: Observable<number>;
  itemCount: number;

  constructor(
    private accountFacade: AccountFacade,
    private salesRepFacade: SalesRepFacade,
    private appFacade: AppFacade,
    private checkoutFacade: CheckoutFacade
  ) {}

  ngOnInit(): void {
    this.loading$ = this.salesRepFacade.salesRepLoading$;
    this.deviceType$ = this.appFacade.deviceType$;
    this.itemsCount$ = this.checkoutFacade.basketItemCount$;
    this.itemsCount$.pipe(takeUntil(this.destroy$)).subscribe(count => {
      this.itemCount = count;
    });
    this.reloadConfig();
  }

  reloadConfig() {
    this.user$ = this.accountFacade.user$;
    this.customer$ = this.accountFacade.customer$;
    combineLatest([this.user$, this.customer$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([user, currentCustomer]) => {
        this.customersToDisplay = user?.availableSoldTos
          ?.filter((customer: customerToDisplay) => customer.customerNo !== currentCustomer.customerNo)
          .map((customer: customerToDisplay) => ({
            customerNo: customer.customerNo,
            companyName: `${customer.companyName}(${customer.customerNo})`,
          }));
        this.currentCustomer = `${currentCustomer?.companyName}(${currentCustomer?.customerNo})`;
      });
  }
  onSelection(selectedItem: customerToDisplay) {
    if (this.itemCount && this.itemCount !== 0) {
      if (selectedItem) {
        const confirmationModal = this.confirmationModal();
        if (confirmationModal) {
          confirmationModal.customerID = selectedItem.customerNo;
          confirmationModal.customerName = selectedItem.companyName;
          confirmationModal.show();
        } else {
          this.onConfirmSwitch(selectedItem.customerNo);
        }
      }
    } else {
      this.onConfirmSwitch(selectedItem.customerNo);
    }
  }

  onCancelSwitch() {
    const customerSelect = this.customerSelect();
    if (customerSelect) {
      customerSelect.clearModel();
    }
  }

  onConfirmSwitch(customerNo: string) {
    this.salesRepFacade.switchCustomer(customerNo);
    const customerSelect = this.customerSelect();
    if (customerSelect) {
      customerSelect.clearModel();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
